/**
 * AppCatalog — every app this person may open here, at /apps/catalog.
 *
 * The store view behind My apps (/apps): each card says what the app is for
 * and whether it is in My apps, with one control to add it or take it off,
 * and a way to open it either way. Nothing is bought or installed: the list
 * is the apps the workspace already offers this person (module, flag,
 * entitlement, workspace kind — useAccessibleApps), and an app they may not
 * open is absent, never shown locked (C Mode rule 2, Doc 31).
 *
 * Adding and taking off are kept on the server (set_my_app, migration 1064).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Plus, Search } from 'lucide-react';
import { APP_ACCENT_CLASSES, type AppDefinition } from './registry';
import { groupApps, isLauncherApp, WORKSPACE_TYPE_LABELS } from './appsHomeSections';
import { useMyApps } from './myApps';
import { AppsNav } from './AppsNav';
import { EmptyState } from './components/primitives';
import { TextInput } from '@/components/ds';
import { useMobileExperience } from '../mobile/featureFlag';
import { useWorkspaceType } from '@/lib/workspaceModulesService';

function matches(app: AppDefinition, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [app.name, app.tagline, app.job].some((text) => text.toLowerCase().includes(q));
}

function CatalogCard({
  app,
  kept,
  onToggle,
}: {
  app: AppDefinition;
  kept: boolean;
  onToggle: () => void;
}) {
  const accent = APP_ACCENT_CLASSES[app.accent];
  return (
    <article className="flex h-full flex-col border border-[var(--rule)] bg-[var(--surface)] p-4">
      <div className="flex items-start gap-3">
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-heading font-semibold text-lg ${accent.chipBg} ${accent.chipText}`}
          aria-hidden
        >
          {app.name.charAt(0)}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-semibold text-[var(--text-pri)]">{app.name}</h3>
          <p className="mt-0.5 font-body text-sm text-[var(--text-sec)]">{app.tagline}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-2 pt-1">
        <Link
          to={app.routeFamily}
          className="font-body text-sm text-[var(--text-sec)] underline-offset-2 hover:text-[var(--text-pri)] hover:underline"
        >
          Open
        </Link>
        {kept ? (
          <span className="inline-flex items-center gap-2">
            <span className="inline-flex items-center gap-1 font-body text-xs text-[var(--text-ter)]">
              <Check className="h-3.5 w-3.5" />
              In My apps
            </span>
            <button
              type="button"
              onClick={onToggle}
              aria-label={`Take ${app.name} off My apps`}
              className="rounded-full border border-[var(--rule)] px-3 py-1 font-body text-xs font-medium text-[var(--text-sec)] hover:border-[var(--rule-bright)] hover:text-[var(--text-pri)]"
            >
              Take off
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            aria-label={`Add ${app.name} to My apps`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--slate)] px-3 py-1 font-body text-xs font-medium text-white hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        )}
      </div>
    </article>
  );
}

export default function AppCatalog() {
  const isMobile = useMobileExperience();
  const { ready, catalog, isKept, setKept, error } = useMyApps();
  const workspaceType = useWorkspaceType();
  const [query, setQuery] = useState('');

  const offered = useMemo(() => catalog.filter((app) => isLauncherApp(app, isMobile)), [catalog, isMobile]);
  const shown = offered.filter((app) => matches(app, query));
  const groups = groupApps(shown);
  const keptCount = offered.filter(isKept).length;

  return (
    <div className="mx-auto w-full max-w-5xl">
      <AppsNav
        title="App catalog"
        lede={
          workspaceType
            ? `Every app you can use in ${WORKSPACE_TYPE_LABELS[workspaceType]} workspaces. Add the ones you want to My apps.`
            : 'Every app you can use here. Add the ones you want to My apps.'
        }
      />
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <label className="relative block w-full sm:max-w-xs">
          <span className="sr-only">Search apps</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-ter)]" />
          <TextInput
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search apps"
            className="pl-9"
          />
        </label>
        {ready && offered.length > 0 ? (
          <p className="font-body text-sm text-[var(--text-ter)]">
            {keptCount} of {offered.length} in My apps
          </p>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="mt-3 font-body text-sm text-[var(--coral)]">
          Your choice was not saved: {error}
        </p>
      ) : null}
      {ready
        ? groups.map((group) => (
            <section key={group.title} className="mt-8">
              <h2 className="font-heading font-semibold text-base text-[var(--text-pri)]">{group.title}</h2>
              {group.hint ? <p className="mt-0.5 font-body text-sm text-[var(--text-ter)]">{group.hint}</p> : null}
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.apps.map((app) => {
                  const kept = isKept(app);
                  return (
                    <CatalogCard
                      key={app.appId}
                      app={app}
                      kept={kept}
                      onToggle={() => void setKept(app.appId, !kept)}
                    />
                  );
                })}
              </div>
            </section>
          ))
        : null}
      {ready && offered.length > 0 && shown.length === 0 ? (
        <div className="mt-8">
          <EmptyState title={`No app matches “${query.trim()}”`} hint="Try a word from what you want to do." />
        </div>
      ) : null}
      {ready && offered.length === 0 ? (
        <div className="mt-8">
          <EmptyState title="No apps are available to you here yet" hint="Ask a workspace admin for access." />
        </div>
      ) : null}
    </div>
  );
}
